import { useCallback, useMemo } from "react";
import type { DocumentNode } from "@apollo/client";
import { useMutation } from "@apollo/client";
import { showSnackbar, showErrorSnackbar } from "@/lib/snackbarUtils";
import useCurrentUser from "@/hooks/useCurrentUser";
import useCurrentBackofficeUser from "@/hooks/useCurrentBackofficeUser";
import { useCanManageTasksBool } from "@/hooks/useCapabilities";
import type { CRMTask } from "@/types";
import type { CommentAuthor, CommentData } from "@/components/CommentList";
import mapTaskComments from "@/pages/tasks/_utils/mapTaskComments";
import {
  CREATE_COMMENT_MUTATION,
  UPDATE_COMMENT_MUTATION,
  DELETE_COMMENT_MUTATION,
  type CreateCommentResponse,
  type CreateCommentVariables,
  type UpdateCommentResponse,
  type UpdateCommentVariables,
  type DeleteCommentResponse,
  type DeleteCommentVariables
} from "./mutations";

interface UseTaskCommentsResult {
  comments: CommentData[];
  currentUser: CommentAuthor | undefined;
  onAddComment: ((content: string) => Promise<void>) | undefined;
  onSaveEdit: ((commentId: string, content: string) => Promise<void>) | undefined;
  onDelete: ((commentId: string) => Promise<boolean>) | undefined;
}

/**
 * Wires a CRMTask's comments into the shape CommentList expects, including
 * add/edit/delete handlers, permission gating, and refetch behavior. When the
 * current user lacks TASKS.manageTasks, `currentUser`, `onAddComment`,
 * `onSaveEdit`, and `onDelete` are all `undefined`, rendering CommentList
 * read-only.
 */
export default function useTaskComments(task: CRMTask, refetchQueries: DocumentNode[] = []): UseTaskCommentsResult {
  const canManageTasks = useCanManageTasksBool();

  const { user } = useCurrentUser();
  const backofficeUser = useCurrentBackofficeUser();
  const currentUser: CommentAuthor | undefined = backofficeUser
    ? { id: backofficeUser.id, name: backofficeUser.name, photo: user?.picture }
    : undefined;

  const [createComment] = useMutation<CreateCommentResponse, CreateCommentVariables>(CREATE_COMMENT_MUTATION, {
    refetchQueries,
    awaitRefetchQueries: true
  });

  const [updateComment] = useMutation<UpdateCommentResponse, UpdateCommentVariables>(UPDATE_COMMENT_MUTATION, {
    refetchQueries,
    awaitRefetchQueries: true
  });

  const [deleteComment] = useMutation<DeleteCommentResponse, DeleteCommentVariables>(DELETE_COMMENT_MUTATION, {
    refetchQueries,
    awaitRefetchQueries: true
  });

  const handleAddComment = useCallback(
    async (content: string) => {
      try {
        const result = await createComment({
          variables: { input: { content, commentableId: task.id, commentableType: "TASK" } }
        });
        if (result.data?.createComment?.success) {
          showSnackbar("Comment added");
        } else {
          const commentErrorMessage = result.data?.createComment?.message;
          if (commentErrorMessage) {
            showErrorSnackbar(commentErrorMessage);
          } else {
            showErrorSnackbar("Comment not added", { subtitle: "Please try again." });
          }
        }
      } catch {
        showErrorSnackbar("Comment not added", { subtitle: "Please try again." });
      }
    },
    [task.id, createComment]
  );

  const handleSaveEdit = useCallback(
    async (commentId: string, content: string) => {
      try {
        const result = await updateComment({
          variables: { input: { id: commentId, content } }
        });
        if (result.data?.updateComment?.success) {
          showSnackbar("Comment updated");
        } else {
          const commentErrorMessage = result.data?.updateComment?.message;
          if (commentErrorMessage) {
            showErrorSnackbar(commentErrorMessage);
          } else {
            showErrorSnackbar("Comment not updated", { subtitle: "Please try again." });
          }
        }
      } catch {
        showErrorSnackbar("Comment not updated", { subtitle: "Please try again." });
      }
    },
    [updateComment]
  );

  const handleDelete = useCallback(
    async (commentId: string): Promise<boolean> => {
      try {
        const result = await deleteComment({ variables: { commentId } });
        if (result.data?.deleteComment?.success) {
          showSnackbar("Comment deleted");
          return true;
        }
        return false;
      } catch {
        return false;
      }
    },
    [deleteComment]
  );

  const comments: CommentData[] = useMemo(() => mapTaskComments(task.comments), [task.comments]);

  return {
    comments,
    currentUser: canManageTasks ? currentUser : undefined,
    onAddComment: canManageTasks ? handleAddComment : undefined,
    onSaveEdit: canManageTasks ? handleSaveEdit : undefined,
    onDelete: canManageTasks ? handleDelete : undefined
  };
}
